import { useState, useEffect, useCallback } from "react";

export default function GalleryLightbox({ images = [], title = "Galleri" }) {
  const [activeIndex, setActiveIndex] = useState(null);

  const handleKeyDown = useCallback((e) => {
    if (activeIndex === null) return;
    if (e.key === "Escape") setActiveIndex(null);
    if (e.key === "ArrowRight") {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }
    if (e.key === "ArrowLeft") {
      setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  }, [activeIndex, images.length]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    if (activeIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [activeIndex, handleKeyDown]);

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-12 text-concrete-500">
        <i className="fa-regular fa-images text-4xl mb-3 block"></i>
        <p>Ingen billeder tilgængelige i dette galleri.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Grid of Images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img, idx) => {
          // Apply Cloudflare resizing for the grid thumbnails (~600px width for crisp retina)
          const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" || window.location.hostname.includes(".local"));
          const thumbUrl = img.startsWith("https://assets.auxodev.dk") && !isLocal
            ? img.replace("/assets/", "/cdn-cgi/image/width=600,quality=85,format=auto/assets/")
            : img;

          return (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className="group relative aspect-4/3 overflow-hidden rounded-lg bg-concrete-100 shadow-sm transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-blue-600"
            >
              <img
                src={thumbUrl}
                alt={`${title} - Billede ${idx + 1}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-brand-blue-900/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
                <span className="rounded-full bg-white/90 p-3 text-brand-blue-900 shadow-md transform translate-y-2 transition-transform duration-300 group-hover:translate-y-0">
                  <i className="fa-solid fa-magnifying-glass-plus text-lg"></i>
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox Overlay */}
      {activeIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-blue-950/95 p-4 backdrop-blur-sm">
          {/* Close button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark text-2xl"></i>
          </button>

          {/* Prev button */}
          <button
            onClick={() => setActiveIndex((prev) => (prev - 1 + images.length) % images.length)}
            className="absolute left-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-xl"></i>
          </button>

          {/* Active Image */}
          <div className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-lg">
            {/* We use the original high-res image for the lightbox, but optimized with format=auto */}
            <img
              src={images[activeIndex].replace("/assets/", "/cdn-cgi/image/width=1600,quality=90,format=auto/assets/")}
              alt={`${title} - Stor visning ${activeIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 text-center text-white">
              <p className="text-sm font-medium">
                {title} — Billede {activeIndex + 1} af {images.length}
              </p>
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={() => setActiveIndex((prev) => (prev + 1) % images.length)}
            className="absolute right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}
